import type {ReactElement, ReactNode} from 'react';
import {useRef} from 'react';
import type {GeneratedImageLightboxHeaderContentProps} from './generated-image-lightbox-header-content.types';
import {MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID} from './generated-image-lightbox-header-content.types';
import styles from './generated-image-lightbox-header-content.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@/components/dropdown-v2/dropdown.types';
import {openAiChatImageDownloadModal} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';
import {PublishingOptions} from '@Components/ai-chat-window/components/publishing-options/publishing-options';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import type {GeneratedImagePartActionsLoading} from '@/ai-chat/providers/ai-chat-provider.types';
import {LoadingStates} from '@Utils/loading.util';
import {usePublishChatModelGeneratedImage} from '@Components/ai-chat-window/hooks/usePublishChatModelGeneratedImage';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7/controlled-list-item-type-7';
import {useResetAiActionLoadingStatesOnModalClose} from '@Components/ai-chat-window/hooks/useResetAiActionLoadingStatesOnModalClose';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';
import {useImageEditMenu} from '@Components/ai-chat-window/hooks/useImageEditMenu';
import {AiChatCopyImageSource, GA4EventName} from '@Libraries/ga-events';
import {CopyAiChatImageButton} from '@Components/ai-chat-window/components/copy-ai-chat-image-button/copy-ai-chat-image-button';
import {TooltipPlacement} from '@Components/icon-with-tooltip/icon-with-tooltip.types';

export function GeneratedImageLightboxHeaderContent({
  onClose,
  hashedId,
  chatId,
  messageId,
  canWrite,
  onReattach,
  currentImage,
  currentImageSrc,
  hasLayeredPoster,
}: GeneratedImageLightboxHeaderContentProps): ReactElement | null {
  const {state, actions} = useAiChat();
  const lightboxHeaderRef = useRef<HTMLDivElement>(null);
  const {isSmall} = useAiChatSize();
  const {publishToSocialMedia, publishAsEmailCampaign, promoteAsEvent} = usePublishChatModelGeneratedImage(chatId, hashedId);

  useResetAiActionLoadingStatesOnModalClose(chatId, hashedId);

  const getLoadingStatesForImageActions = (): GeneratedImagePartActionsLoading => {
    return state.getLoadingStatesForImageActions(chatId, hashedId);
  };

  const handleCloseLightbox = (): void => {
    onClose();
  };

  const handleOpenChat = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_MY_AI_DESIGN_OPEN_IN_CHAT);
    actions.setCurrentChatId(chatId, {messageId});
  };

  const handleDownload = (): void => {
    const hashedIdValue: string | undefined = hashedId;
    if (!hashedIdValue) {
      return;
    }

    openAiChatImageDownloadModal(hashedIdValue, chatId);
  };

  const isDisabled =
    getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().publishingLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().makeTextEditableLoadingState === LoadingStates.LOADING;

  const imageEditMenu = useImageEditMenu({
    chatId,
    hashedId,
    hasLayeredPoster,
    moreOptionsPanelId: MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID,
    isDisabled: isDisabled || !canWrite,
  });

  const getMoreOptionsContent = (): ReactElement => {
    if (imageEditMenu.isEditSubmenuScreen) {
      return imageEditMenu.getEditSubmenu();
    }

    return (
      <>
        {imageEditMenu.getMobileEditMenuRow()}
        {getReattachRow()}
        {getOpenChatRow()}
      </>
    );
  };

  const getReattachRow = (): ReactElement | null => {
    if (state.isAssetsView) {
      return null;
    }

    return (
      <ControlledListItemType7
        id="reattach"
        text={window.i18next.t('pmwjs_reattach')}
        icon="icon-paperclip"
        onClick={(): void => {
          onReattach(currentImage, currentImageSrc);
          handleCloseLightbox();
        }}
        isDisabled={isDisabled}
        className="radius-8"
      />
    );
  };

  const getOpenChatRow = (): ReactElement | null => {
    if (!state.isAssetsView || !messageId) {
      return null;
    }

    return (
      <ControlledListItemType7
        id="open-chat"
        text={window.i18next.t('pmwjs_open_chat')}
        icon="icon-external-link"
        onClick={handleOpenChat}
        isDisabled={isDisabled}
        className="radius-8"
      />
    );
  };

  useUpdateMoreOptionsModal(
    getMoreOptionsContent(),
    [
      imageEditMenu.editScreen,
      imageEditMenu.isDailyLimitReached,
      isDisabled,
      getLoadingStatesForImageActions().makeTextEditableLoadingState,
      getLoadingStatesForImageActions().makeDesignEditableLoadingState,
    ],
    {panelIdOpenedInModal: MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID, additionalPanelProps: imageEditMenu.getMoreOptionsModalHeaderProps()}
  );

  const getMoreOptionsDropdown = (): ReactNode => {
    return (
      <Dropdown
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_more_options')}
        panelIdForModal={MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID}
        popupClassName={styles.actionPopup}
        className={styles.positionUnset}
        doCloseOnInnerClick
        selector={<Icon icon="icon-more-h" size={IconSize.SIZE_ICON_16} type={IconType.DARK_SECONDARY} shape={IconShape.SQUARE} isDisabled={isDisabled} />}
        popup={getMoreOptionsContent()}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
      />
    );
  };

  const getCopyImageAction = (): ReactNode => {
    return (
      <CopyAiChatImageButton
        key={hashedId}
        hashedId={hashedId}
        source={state.isAssetsView ? AiChatCopyImageSource.MY_DESIGNS : AiChatCopyImageSource.LIGHTBOX}
        iconProps={{type: IconType.DARK_SECONDARY, shape: IconShape.SQUARE}}
        tooltipPlacement={TooltipPlacement.BOTTOM}
        tooltipContainer={lightboxHeaderRef}
      />
    );
  };

  const getDownloadAction = (): ReactNode => {
    if (isSmall) {
      return (
        <Icon
          icon="icon-download"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          onClick={handleDownload}
          isDisabled={isDisabled}
          isLoading={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
        />
      );
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_download')}
        size={ButtonSize.SMALL}
        type={ButtonType.SECONDARY} onDark
        icon="icon-download"
        onClick={handleDownload}
        isLoading={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
        disabled={isDisabled}
      />
    );
  };

  const getEditDropdown = (): ReactNode => {
    return (
      <Dropdown
        popUpHasCustomWidth
        popupClassName={styles.editPopup}
        className={styles.positionUnset}
        doCloseOnInnerClick
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        onOpen={imageEditMenu.refreshDailyLimitOnMenuOpen}
        selector={
          <Button
            text={window.i18next.t('pmwjs_edit')}
            size={ButtonSize.SMALL}
            type={ButtonType.TERTIARY} onDark
            icon="icon-pencil"
            customClasses={'spacing-p-l-4 spacing-p-r-4'}
            disabled={isDisabled || !canWrite}
          />
        }
        popup={imageEditMenu.getEditSubmenu({isMobile: false})}
      />
    );
  };

  const getEditAndReattachActions = (): ReactNode => {
    if (isSmall) {
      return getMoreOptionsDropdown();
    }

    return (
      <>
        {getEditDropdown()}
        {getReattachButton()}
        {getOpenChatButton()}
      </>
    );
  };

  const getReattachButton = (): ReactElement | null => {
    if (state.isAssetsView) {
      return null;
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_reattach')}
        size={ButtonSize.SMALL}
        type={ButtonType.TERTIARY} onDark
        icon="icon-paperclip"
        onClick={(): void => {
          onReattach(currentImage, currentImageSrc);
          handleCloseLightbox();
        }}
        disabled={isDisabled}
      />
    );
  };

  const getOpenChatButton = (): ReactElement | null => {
    if (!state.isAssetsView || !messageId) {
      return null;
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_open_chat')}
        size={ButtonSize.SMALL}
        type={ButtonType.TERTIARY} onDark
        icon="icon-external-link"
        onClick={handleOpenChat}
        disabled={isDisabled}
      />
    );
  };

  const getLightboxHeader = (): ReactElement => {
    return (
      <div className={styles.lightboxHeader} ref={lightboxHeaderRef}>
        <Icon
          icon="icon-arrow-left"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_GHOST}
          ariaLabel={window.i18next.t('pmwjs_back')}
          shape={IconShape.SQUARE}
          onClick={handleCloseLightbox}
          className={styles.backButton}
        />
        <div className={`flex-h-row gap-2 ${styles.headerActions}`}>
          {getEditAndReattachActions()}
          {getCopyImageAction()}
          {getDownloadAction()}
          <Dropdown
            showModalForSmallScreen
            modalTitle={window.i18next.t('pmwjs_publish')}
            popupClassName={styles.actionPopup}
            className={styles.positionUnset}
            selector={
              <Button
                text={window.i18next.t('pmwjs_publish')}
                size={ButtonSize.SMALL}
                type={ButtonType.SECONDARY}
                icon="icon-publish-arrow"
                isLoading={getLoadingStatesForImageActions().publishingLoadingState === LoadingStates.LOADING}
                disabled={isDisabled || !canWrite}
              />
            }
            popup={
              <PublishingOptions
                chatId={chatId}
                hashedId={hashedId}
                loadingStates={getLoadingStatesForImageActions()}
                publishToSocialMedia={publishToSocialMedia}
                publishAsEmailCampaign={publishAsEmailCampaign}
                promoteAsEvent={promoteAsEvent}
              />
            }
            alignment={ALIGNMENT_TYPE.BOTTOM_END}
          />
        </div>
      </div>
    );
  };

  return getLightboxHeader();
}
